import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, MessageSquare, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHero } from "@/components/site/Section";
import { api, errMsg } from "@/lib/api";

const TOPICS = ["General", "Sales & Pricing", "Support", "Partnerships", "Careers", "Privacy / GDPR request"];

export default function Contact() {
  const [params] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", company: "", phone: "", topic: params.get("topic") || "General", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", f);
      setSent(true);
      toast.success("Thanks! We'll get back to you shortly.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Contact" title={<>Let's talk about <span className="text-gradient">your marketing</span></>}
        copy="Questions about plans, enterprise access, support or data protection? Send us a message and the Sliance team will respond by email." />
      <section className="py-20">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-4">
            {[[Mail, "Enquiries & sales", "contact@sliance.com"], [LifeBuoy, "Support", "support@sliance.com"], [MessageSquare, "Privacy / GDPR", "legal@sliance.com"]].map(([I, t, e]) => (
              <a key={t} href={`mailto:${e}`} className="card-box p-6 flex gap-4 lift" data-testid={`contact-card-${t.split(" ")[0].toLowerCase()}`}>
                <I className="h-6 w-6 text-[#7c3aed]" /><div><div className="font-semibold">{t}</div><div className="text-sm text-muted-foreground">{e}</div></div>
              </a>
            ))}
          </div>
          <div className="lg:col-span-8">
            {sent ? (
              <div className="card-box p-10" data-testid="contact-success">
                <h2 className="text-2xl font-bold">Message received</h2>
                <p className="mt-3 text-muted-foreground">Thank you for reaching out. Our team will reply to {f.email} soon.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="card-box p-6 md:p-10 grid sm:grid-cols-2 gap-5" data-testid="contact-form">
                <div className="space-y-2"><Label>Name *</Label><Input required value={f.name} onChange={set("name")} data-testid="contact-name-input" /></div>
                <div className="space-y-2"><Label>Email *</Label><Input required type="email" value={f.email} onChange={set("email")} data-testid="contact-email-input" /></div>
                <div className="space-y-2"><Label>Company</Label><Input value={f.company} onChange={set("company")} data-testid="contact-company-input" /></div>
                <div className="space-y-2"><Label>Phone</Label><Input value={f.phone} onChange={set("phone")} data-testid="contact-phone-input" /></div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Topic</Label>
                  <Select value={f.topic} onValueChange={(v) => setF({ ...f, topic: v })}>
                    <SelectTrigger data-testid="contact-topic-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TOPICS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2"><Label>Message *</Label><Textarea required minLength={5} rows={6} value={f.message} onChange={set("message")} data-testid="contact-message-input" /></div>
                <p className="sm:col-span-2 text-xs text-muted-foreground">We use your details only to respond to this enquiry, as described in our Privacy Policy.</p>
                <button disabled={busy} className="btn-brand sm:col-span-2 disabled:opacity-60" data-testid="contact-submit-btn">{busy ? "Sending..." : "Send message"}</button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
